import {
  AttachMoney,
  Inventory2,
  People,
  Percent,
  Sell,
  ShoppingCart,
} from "@mui/icons-material";
import { Box, Grid, Stack } from "@mui/material";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import { StoreRecentOrders, table10Rows, table5Rows } from "features/analytics";
import { ProductTrendProvider } from "features/products";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useProducts } from "Hooks/useProducts";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import StoreBestProductHighlight from "./StoreBestProductHighlight";
import StoreCustomerInsights from "./StoreCustomerInsights";
import {
  StoreCustomersPieChart,
  StoreOrderStatusPieChart,
} from "./StoreOverviewPieCharts";
import EcommerceDataNoticeDialog from "features/project/components/EcommerceDataNoticeDialog";
import { QuickInsightCarousel } from "features/quickInsights";
import { useQuickInsightResults } from "features/quickInsights/hooks/useQuickInsightResults";
import {
  CHART_KEY_STORE_OVERVIEW_AVERAGE_ORDER_VALUE,
  CHART_KEY_STORE_OVERVIEW_CUSTOMERS,
  CHART_KEY_STORE_OVERVIEW_ITEMS_SOLD,
  CHART_KEY_STORE_OVERVIEW_ORDERS,
  CHART_KEY_STORE_OVERVIEW_REPEAT_CUSTOMER_RATE,
  CHART_KEY_STORE_OVERVIEW_REVENUE_OVER_TIME,
  CHART_KEY_STORE_OVERVIEW_TOP_PRODUCTS,
  CHART_KEY_STORE_OVERVIEW_TOTAL_REVENUE,
} from "features/analytics/constants/chartKeys";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";

function StoreOverviewPage() {
  const history = useHistory();
  const { selectedProject, selectedClient } = useSelectedProject();
  const { products } = useProducts();
  const { pathByName } = useProtectedRoutes();
  const { quickInsightResults, loading: quickInsightResultsLoading } =
    useQuickInsightResults();

  const toProduct = useCallback(
    (productName: string) => {
      const productId = products.find((p) => p.name === productName)?.id;
      if (!productId || !selectedProject?.id || !selectedClient?.id) {
        return;
      }

      history.push(
        pathByName(RouteNames.PRODUCT_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        }),
        { selectedProductId: productId },
      );
    },
    [history, pathByName, products, selectedClient?.id, selectedProject?.id],
  );

  return (
    <ProductTrendProvider chartAttributes={[]}>
      <Grid container spacing={AnalyticsSpacing} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_OVERVIEW_TOTAL_REVENUE}
            icon={<AttachMoney />}
            chart={{
              name: "Total Revenue",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_REVENUE],
                },
              ],
              displayOptions: {
                statTitle: "Total Revenue",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_OVERVIEW_ORDERS}
            icon={<ShoppingCart />}
            chart={{
              name: "Orders",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALES_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Orders",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_OVERVIEW_ITEMS_SOLD}
            icon={<Inventory2 />}
            chart={{
              name: "Items Sold",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALE_ITEMS_QUANTITY],
                },
              ],
              displayOptions: {
                statTitle: "Items Sold",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_OVERVIEW_AVERAGE_ORDER_VALUE}
            icon={<Sell />}
            chart={{
              name: "Average Order Value",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_SALE_VALUE],
                },
              ],
              displayOptions: {
                statTitle: "Average Order Value",
              },
              comparison: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={4} sx={{ minHeight: 400 }}>
          <StoreBestProductHighlight />
        </Grid>

        <Grid item xs={12} lg={8} sx={{ minHeight: 400 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_STORE_OVERVIEW_REVENUE_OVER_TIME}
            ignoreAttrModel
            chart={{
              name: "Revenue Over Time",
              dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_REVENUE],
                  chart: {
                    [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                availableMetrics: [
                  ApiMetric.METRIC_REVENUE,
                  ApiMetric.METRIC_SALES_COUNT,
                  ApiMetric.METRIC_AVERAGE_SALE_VALUE,
                ],
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <QuickInsightCarousel
            insights={quickInsightResults || []}
            loading={quickInsightResultsLoading}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <Stack spacing={AnalyticsSpacing} sx={{ height: "100%" }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <StandardAnalyticsChartStatCard
                chartKey={CHART_KEY_STORE_OVERVIEW_CUSTOMERS}
                icon={<People />}
                chart={{
                  name: "Customers",
                  dimension: [ApiDimension.DIMENSION_NOT_SET],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.STAT_CARD,
                  attributes: [],
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_CUSTOMERS_COUNT],
                    },
                  ],
                  displayOptions: {
                    statTitle: "Customers",
                  },
                  comparison: true,
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <StandardAnalyticsChartStatCard
                chartKey={CHART_KEY_STORE_OVERVIEW_REPEAT_CUSTOMER_RATE}
                icon={<Percent />}
                chart={{
                  name: "Repeat Customer %",
                  dimension: [ApiDimension.DIMENSION_NOT_SET],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.STAT_CARD,
                  attributes: [],
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_REPEAT_CUSTOMER_RATE],
                    },
                  ],
                  displayOptions: {
                    statTitle: "Repeat Customer %",
                  },
                  comparison: true,
                }}
              />
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4.5} sx={{ minHeight: 400 }}>
          <StoreCustomersPieChart />
        </Grid>
        <Grid item xs={12} md={4.5} sx={{ minHeight: 400 }}>
          <StoreOrderStatusPieChart />
        </Grid>

        <Grid item xs={6} sx={{ minHeight: table5Rows }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_STORE_OVERVIEW_TOP_PRODUCTS}
            onClick={{
              [ApiDimension.DIMENSION_PRODUCT_NAME]: [
                {
                  text: "Go To Product",
                  fn: (productName) => toProduct(productName || ""),
                },
              ],
            }}
            chart={{
              name: "Top Products",
              dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PRODUCT_REVENUE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                  ],
                },
              ],
              displayOptions: {
                noAttributionToggle: true,
                noFilters: true,
                rowsPerPage: 0,
              },
            }}
          />
        </Grid>
        <Grid item xs={6} sx={{ minHeight: table5Rows }}>
          <StoreCustomerInsights />
        </Grid>

        <Grid item xs={12} sx={{ minHeight: table10Rows }}>
          <StoreRecentOrders />
        </Grid>
      </Grid>
      <EcommerceDataNoticeDialog />
    </ProductTrendProvider>
  );
}

export default StoreOverviewPage;
